import type { CategoryBadgeTone } from '@/components/ui/chips/category-badge';
import type { RunOutcome, RunStatus } from '@/types/custom-playbooks';

/** How a run's status reads and which tint it takes, shared by every run list. */
export const RUN_TONE: Record<RunStatus, CategoryBadgeTone> = {
  pending: 'blue',
  running: 'blue',
  completed: 'green',
  completed_with_errors: 'amber',
  failed: 'amber',
  cancelled: 'slate',
  paused_credits: 'amber',
};

export const RUN_LABEL: Record<RunStatus, string> = {
  pending: 'Queued',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  failed: 'Needs attention',
  cancelled: 'Cancelled',
  paused_credits: 'Paused, out of credits',
};

/** Not moving: finished, or paused until the org tops up. */
const TERMINAL: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
  'paused_credits',
]);

/** Still moving: the list polls while any run is. */
export function isRunLive(status: RunStatus): boolean {
  return !TERMINAL.has(status);
}

type RunState = { status: RunStatus; outcome?: RunOutcome | null };

/** A finished run whose every record was skipped, as the server decided. */
export function isRunSkipped(run: RunState): boolean {
  return run.status === 'completed' && run.outcome?.status === 'skipped';
}

/** How a run reads in a list: Skipped for a run with nothing to do. */
export function runLabel(run: RunState): string {
  return isRunSkipped(run) ? 'Skipped' : RUN_LABEL[run.status];
}

/** Codes that say why a record was skipped, not that anything failed
 *  (cleanse-api `run_remedies.SOURCE_OUTCOMES`). */
const SOURCE_OUTCOMES: ReadonlySet<string> = new Set([
  'find_company_identity_mismatch',
]);

/** The run's errors that are failures: a completed run keeps why records
 *  were skipped among its errors, and those are not. */
export function runFailures<E extends { code: string }>(
  run: RunState & { errors: E[] }
): E[] {
  if (run.status !== 'completed') return run.errors;
  const skipped = isRunSkipped(run) ? run.outcome?.code : undefined;
  return run.errors.filter(
    (e) => !SOURCE_OUTCOMES.has(e.code) && e.code !== skipped
  );
}
